import { useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../../../../utils/constants';
import useCreateResponse from 'hooks/ai-forums/useCreateResponse';

interface Props {
  parentId: string;
  postId: string;
  onSuccess?: () => void;
}

function CommentInput({ parentId, postId, onSuccess }: Props) {
  const [value, setValue] = useState('');
  const { createResponse, isPending } = useCreateResponse();

  const handleSubmit = async () => {
    if (!value.trim()) return;

    try {
      await createResponse(
        postId,
        { content: value },
        {
          onSuccess: (response) => {
            setValue('');
            onSuccess?.();
            window.location.reload();
          },
          onError: (errors) => {
            console.error('Failed to submit reply:', errors);
          },
        },
        parentId,
      );
    } catch (validationErrors) {
      console.error('Validation errors:', validationErrors);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div css={containerStyles}>
      <input
        type="text"
        placeholder="Reply to comment..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isPending}
        css={inputStyles}
      />
      <button css={buttonStyles} onClick={handleSubmit} disabled={isPending || !value.trim()}>
        {isPending ? 'Replying...' : 'Reply'}
      </button>
    </div>
  );
}

const containerStyles = css({
  marginTop: '16px',
  marginBottom: '8px',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  [MOBILE.MEDIA_QUERY]: {
    marginTop: '6px',
    marginBottom: '6px',
    gap: '6px',
  },
});

const inputStyles = css({
  width: '70%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: `1px solid ${token('color.border')}`,
  fontSize: '14px',
  fontFamily: 'Inter, sans-serif',
  color: token('color.text'),
  backgroundColor: token('elevation.surface'),
  '&:focus': {
    outline: 'none',
    borderColor: token('color.border.focused'),
    boxShadow: `0 0 0 2px ${token('color.border.focused')}`,
  },
  '&::placeholder': {
    color: token('color.text.subtle'),
  },
  [MOBILE.MEDIA_QUERY]: {
    width: '65%',
    padding: '6px 10px',
    fontSize: '13px',
  },
});

const buttonStyles = css({
  padding: '8px 16px',
  borderRadius: '6px',
  background: token('color.background.brand.bold'),
  color: token('color.text.inverse'),
  border: 'none',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Charlie Text, sans-serif',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    background: token('color.background.brand.bold.hovered'),
  },
  '&:active': {
    background: token('color.background.brand.bold.pressed'),
  },
  '&:disabled': {
    background: token('color.background.disabled'),
    color: token('color.text.disabled'),
    cursor: 'not-allowed',
  },
  [MOBILE.MEDIA_QUERY]: {
    padding: '6px 12px',
    fontSize: '13px',
  },
});

export default CommentInput;
